import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { AlertCircle } from "lucide-react";
import { Modal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { changeAliasApi } from "../../../../api/integrations";

// Modificar el alias de una cuenta vinculada. `account` es la integración tal
// cual viene del backend (companyId/alias/integrationName); `changeAlias` es el
// rename optimista local de la vista (sin refetch, contrato del legacy).
export default function ChangeAliasModal({ open, account, changeAlias, onClose }) {
  const { t } = useTranslation();
  const { companyId, alias, integrationName } = account || {};
  const [newAlias, setNewAlias] = useState(alias || "");
  const [saving, setSaving] = useState(false);
  const inputId = useId();
  const errorId = useId();
  const inputRef = useRef(null);

  const trimmed = (newAlias || "").trim();
  const invalid = trimmed === "" || trimmed === alias;
  // Solo mostramos el error inline si el usuario ya editó el campo: el estado
  // inicial (alias sin cambios) no es un error, solo deshabilita Guardar.
  const dirty = newAlias !== alias;
  const fieldError = !invalid || !dirty
    ? ""
    : trimmed === ""
      ? t("El alias no puede estar vacío.")
      : t("El alias debe ser distinto al actual.");

  // autoFocus del legacy: el Modal enfoca su botón de cerrar por defecto.
  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 60);
    return () => clearTimeout(id);
  }, [open]);

  const handleSave = async () => {
    if (invalid) {
      toast.error(t("El alias no puede estar vacío ni ser igual al anterior"));
      return;
    }
    try {
      setSaving(true);
      // La API captura los errores y resuelve {error}: lo convertimos en
      // rechazo para que toast.promise no anuncie éxito sobre una falla.
      const request = changeAliasApi({ companyId, alias, integrationName, newAlias: trimmed }).then(
        (res) => {
          if (res && "error" in res) {
            const err = new Error("changeAlias failed");
            err.status = res.error?.statusCode;
            err.data = res.error;
            throw err;
          }
          return res;
        },
      );
      // Sin entrada `error`: el catch de abajo es el dueño del toast de error
      // (con ambas, cada fallo apilaba dos toasts por el mismo error).
      await toast.promise(request, {
        pending: t("Modificando..."),
        success: t("¡Alias modificado correctamente!"),
      });

      if (typeof changeAlias === "function") changeAlias(alias, trimmed);
      onClose();
    } catch (error) {
      console.error(error);

      if (error?.status === 404 || error?.response?.status === 404) {
        toast.error(error?.data?.error || error?.response?.data?.error || t("Usuario no encontrado"));
      } else {
        toast.error(t("Ha ocurrido un error al modificar el alias. Por favor, intenta nuevamente."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={t("Modificar alias")}
      description={t("Edita el nombre con el que identificas esta cuenta de {{name}}.", {
        name: integrationName,
      })}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={invalid || saving}
          >
            {saving ? t("Guardando...") : t("Guardar cambios")}
          </Button>
        </>
      }
    >
      <div className="space-y-1.5">
        <FieldLabel htmlFor={inputId}>{t("Alias de la cuenta")}</FieldLabel>
        <Input
          id={inputId}
          ref={inputRef}
          value={newAlias}
          placeholder={t("Ej: Tienda principal")}
          autoComplete="off"
          invalid={!!fieldError}
          aria-invalid={fieldError ? true : undefined}
          aria-describedby={fieldError ? errorId : undefined}
          onChange={(e) => setNewAlias(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !invalid && !saving) handleSave();
          }}
        />
        <p
          id={errorId}
          aria-live="polite"
          className="flex min-h-[1rem] items-center gap-1 text-xs text-status-error"
        >
          {fieldError && (
            <>
              <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {fieldError}
            </>
          )}
        </p>
      </div>
    </Modal>
  );
}
